import { createReducer } from 'redux-create-reducer'
import type { Business } from 'types/public-playlist'
import { TRACK_LOAD_BATCH } from '@disco/constants/ActionTypes'
import {
  UPLOAD_DISCO_USER_LOGIN,
  UPLOAD_DISCO_USER_LOGIN_FAIL,
  UPLOAD_DISCO_USER_LOGIN_REQUIRE_MFA,
  UPLOAD_DISCO_USER_LOGIN_SUCCESS,
  UPLOAD_INSERT_TEMP_ITEMS,
  UPLOAD_REMOVE_TEMP_ITEM,
  UPLOAD_SEND_PLAYLIST,
  UPLOAD_SEND_PLAYLIST_FAIL,
  UPLOAD_SEND_PLAYLIST_SUCCESS,
  UPLOAD_SET_DISCO_PLAYLIST,
  UPLOAD_SET_DISCO_USER,
  UPLOAD_UPDATE_TEMP_ITEM,
} from '@disco/constants/inbox-action-types'
import { TRACK } from '@disco/constants/ItemTypes'
import businessCookie from '@disco/utils/business-cookie'
import { stripFiles } from '@disco/utils/upload'
const initialState = {
  tempItems: [] as any[],
  sending: false,
  sent: false,
  error: null,
  businessList: businessCookie() as Business[],
  discoPlaylist: null,
  discoUser: null,
  userAuth: {
    loggingIn: false,
    loggedIn: false,
    showLoginError: false,
    mfaCodeRequired: false,
    error: null,
  },
}
export type State = typeof initialState
export const uploadReducer = createReducer<State, any>(initialState, {
  [UPLOAD_INSERT_TEMP_ITEMS](state, action) {
    return {
      ...state,
      tempItems: [...state.tempItems, ...stripFiles(action.tempItems)],
    }
  },

  [UPLOAD_REMOVE_TEMP_ITEM](state, action) {
    const itemIndex = state.tempItems.findIndex(tempItem => {
      return tempItem.tempId === action.tempItem.tempId
    })
    if (itemIndex === -1) return state
    return {
      ...state,
      tempItems: [
        ...state.tempItems.slice(0, itemIndex),
        ...state.tempItems.slice(itemIndex + 1),
      ],
    }
  },

  [UPLOAD_UPDATE_TEMP_ITEM](state, action) {
    const itemIndex = state.tempItems.findIndex(tempItem => {
      return tempItem.tempId === action.tempItem.tempId
    })
    // workaround for evaporate uploads that call progress updates after cancellation on removed item
    if (itemIndex === -1) return state
    return {
      ...state,
      tempItems: stripFiles([
        ...state.tempItems.slice(0, itemIndex),
        action.tempItem,
        ...state.tempItems.slice(itemIndex + 1),
      ]),
    }
  },

  [UPLOAD_SEND_PLAYLIST](state) {
    return { ...state, sending: true }
  },

  [UPLOAD_SEND_PLAYLIST_SUCCESS](state, action) {
    // reset all state after sending playlist
    if (action.shouldKeepUploadState) {
      return { ...state, sent: true }
    }

    return { ...initialState, sent: true }
  },

  [UPLOAD_SEND_PLAYLIST_FAIL](state, action) {
    return { ...state, sending: false, sent: false, error: action.error }
  },

  [UPLOAD_SET_DISCO_PLAYLIST](state, { playlist }) {
    return { ...state, discoPlaylist: playlist, error: null }
  },

  [UPLOAD_SET_DISCO_USER](state, { user }) {
    return { ...state, discoUser: user }
  },

  [UPLOAD_DISCO_USER_LOGIN](state) {
    return {
      ...state,
      discoUser: null,
      userAuth: {
        ...state.userAuth,
        showLoginError: false,
        loggingIn: true,
        loggedIn: false,
        error: null,
      },
    }
  },

  [UPLOAD_DISCO_USER_LOGIN_SUCCESS](state, { user }) {
    return {
      ...state,
      discoUser: user,
      userAuth: {
        ...state.userAuth,
        showLoginError: false,
        loggingIn: false,
        loggedIn: true,
        mfaCodeRequired: false,
        error: null,
      },
    }
  },

  [UPLOAD_DISCO_USER_LOGIN_FAIL](state, { error }) {
    return {
      ...state,
      discoUser: null,
      userAuth: {
        ...state.userAuth,
        showLoginError: true,
        loggingIn: false,
        loggedIn: false,
        error,
      },
    }
  },

  [UPLOAD_DISCO_USER_LOGIN_REQUIRE_MFA](state) {
    return {
      ...state,
      userAuth: {
        ...state.userAuth,
        mfaCodeRequired: true,
        loggingIn: false,
        loggedIn: false,
      },
    }
  },

  [TRACK_LOAD_BATCH](state, action) {
    if (!action.list || action.list.length === 0) return state
    // ensure that params consists of tracks only
    const pendingUpdate = action.list.filter(item => item.type === TRACK)
    return {
      ...state,
      tempItems: state.tempItems.map(tempItem => {
        const updatedItem = pendingUpdate.find(
          track => track.id === tempItem.id,
        )
        if (updatedItem) return { ...tempItem, ...updatedItem }
        return tempItem
      }),
    }
  },
})
